import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  alpha,
  useTheme,
  CircularProgress,
  Card,
  CardContent,
  Container,
  Autocomplete,
  Avatar
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Person as PersonIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  AttachMoney as MoneyIcon,
  Assignment as AssignmentIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

const OrderCreation = forwardRef(({ purchaseOrder, onUpdate, isCreatingNew }, ref) => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    order_date: dayjs().format('YYYY-MM-DD'),
    requested_delivery_date: '',
    customer_po_number: '',
    customer_notes: '',
    priority_level: '',
    price_list_id: '',
    payment_terms: '',
    payment_method: '',
    status: 'draft'
  });
  
  const [customers, setCustomers] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [priceListDialogOpen, setPriceListDialogOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [priceListSearchTerm, setPriceListSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Status chip styling (Processing Status) - same as ProformaInvoices.jsx
  const getStatusChip = (status) => {
    const statusConfig = {
      draft: { color: 'default', label: 'Draft', icon: <EditIcon fontSize="small" /> },
      pending_approval: { color: 'warning', label: 'Pending Approval', icon: <ScheduleIcon fontSize="small" /> },
      approved: { color: 'success', label: 'Approved', icon: <CheckCircleIcon fontSize="small" /> },
      in_production: { color: 'info', label: 'In Production', icon: <ScheduleIcon fontSize="small" /> },
      shipped: { color: 'primary', label: 'Shipped', icon: <LocalShippingIcon fontSize="small" /> },
      delivered: { color: 'success', label: 'Delivered', icon: <CheckCircleIcon fontSize="small" /> },
      cancelled: { color: 'error', label: 'Cancelled', icon: <CancelIcon fontSize="small" /> }
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        sx={{
          fontWeight: 500,
          fontSize: '0.75rem',
          height: 24,
          borderRadius: 2,
          '& .MuiChip-icon': {
            fontSize: 14
          }
        }}
      />
    );
  };

  // Format date without timezone issues
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    // If it's already a formatted date string, return it
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    // Parse date string (YYYY-MM-DD) directly without timezone conversion
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        return `${month}/${day}/${year}`;
      }
    }
    // Fallback: use Date but add noon to avoid timezone issues
    const date = new Date(dateString + 'T12:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Permission checks - define early so they can be used in hooks
  const canView = hasPermission('sales.purchase_orders.order_creation.view');
  const canEdit = hasPermission('sales.purchase_orders.order_creation.edit', 'write');

  // Generate Proforma Invoice number - resets to 0001 each year: PI-YYYY-0001
  const generateProformaInvoiceNumber = async () => {
    try {
      // Get current year
      const now = new Date();
      const year = now.getFullYear().toString();

      // Count POs for the current year only
      const yearStart = `${year}-01-01`;
      const yearEnd = `${year}-12-31`;

      const { count, error } = await supabase
        .from('customer_purchase_orders')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', companyId)
        .gte('order_date', yearStart)
        .lte('order_date', yearEnd);

      if (error && error.code !== 'PGRST116') throw error;

      // Start from 0001 for the first PI of the year
      const sequentialNumber = (count || 0) + 1;

      // Format: PI-YYYY-0001
      return `PI-${year}-${String(sequentialNumber).padStart(4, '0')}`;
    } catch (err) {
      console.error('Error generating proforma invoice number:', err);
      const now = new Date();
      const year = now.getFullYear();
      return `PI-${year}-${String(Date.now()).slice(-4)}`;
    }
  };

  // Expose openEditDialog method via ref - JUST LIKE OVERVIEW TAB!
  useImperativeHandle(ref, () => ({
    openEditDialog: async (isNew = false) => {
      if (!canEdit) {
        setError('You do not have permission to edit order creation.');
        return;
      }
      if (isNew) {
        const orderNumber = await generateProformaInvoiceNumber();
        setFormData(prev => ({
          ...prev,
          customer_po_number: orderNumber
        }));
      }
      setEditDialogOpen(true);
    }
  }));

  // Auto-open dialog if creating new - JUST LIKE OVERVIEW TAB!
  useEffect(() => {
    if (isCreatingNew && canEdit) {
      setTimeout(async () => {
        const orderNumber = await generateProformaInvoiceNumber();
        setFormData(prev => ({
          ...prev,
          customer_po_number: orderNumber
        }));
        setEditDialogOpen(true);
      }, 300);
    } else if (isCreatingNew && !canEdit) {
      setError('You do not have permission to create orders.');
    }
  }, [isCreatingNew, canEdit]);

  // Load data
  useEffect(() => {
    fetchCustomers();
    fetchPriceLists();
  }, [companyId]);

  // Load existing data if editing
  useEffect(() => {
    if (purchaseOrder?.id) {
      loadOrderCreationData();
    }
  }, [purchaseOrder, customers]);

  const fetchCustomers = async () => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (error) throw error;
      setCustomers(data || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchPriceLists = async () => {
    try {
      const { data, error } = await supabase
        .from('price_lists')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setPriceLists(data || []);
    } catch (err) {
      console.error('Error fetching price lists:', err);
    }
  };

  const loadOrderCreationData = async () => {
    if (!purchaseOrder?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('po_order_creation')
        .select('*')
        .eq('purchase_order_id', purchaseOrder.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setFormData({
          customer_id: data.customer_id || '',
          order_date: data.order_date || dayjs().format('YYYY-MM-DD'),
          requested_delivery_date: data.requested_delivery_date || '',
          customer_po_number: data.customer_po_number || '',
          customer_notes: data.customer_notes || '',
          priority_level: data.priority_level || 'medium',
          price_list_id: data.price_list_id || '',
          payment_terms: data.payment_terms || 'Net 30',
          payment_method: data.payment_method || '',
          status: purchaseOrder.status || 'draft'
        });
        
        if (data.customer_id) {
          const customer = customers.find(c => c.id === data.customer_id);
          setSelectedCustomer(customer);
        }
      }
    } catch (err) {
      console.error('Error loading order creation data:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    // Validation
    if (!formData.customer_id) {
      setError('Please select a customer');
      return;
    }
    
    if (!formData.order_date) {
      setError('Please select an order date');
      return;
    }

    if (!formData.requested_delivery_date) {
      setError('Please select a requested delivery date');
      return;
    }

    if (!formData.customer_po_number) {
      setError('Please enter a Proforma Invoice Number');
      return;
    }

    if (!formData.priority_level) {
      setError('Please select a priority level');
      return;
    }

    if (!formData.payment_terms) {
      setError('Please select payment terms');
      return;
    }

    if (!formData.payment_method) {
      setError('Please select a payment method');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const customer = customers.find(c => c.id === formData.customer_id);
      
      let poId = purchaseOrder?.id;
      
      // If creating new, INSERT the order first!
      if (isCreatingNew || !poId) {
        const poNumber = formData.customer_po_number || await generateProformaInvoiceNumber();
        
        const { data: newPO, error: poError } = await supabase
          .from('customer_purchase_orders')
          .insert({
            company_id: companyId,
            po_number: poNumber,
            status: formData.status || 'draft',
            current_stage: 'po_creation',
            stage_completed: ['order_creation'],
            customer_id: formData.customer_id,
            customer_name: customer?.name,
            customer_company: customer?.company,
            customer_po_number: formData.customer_po_number || null,
            order_date: formData.order_date && formData.order_date.trim() !== '' ? formData.order_date : null,
            requested_delivery_date: formData.requested_delivery_date && formData.requested_delivery_date.trim() !== '' ? formData.requested_delivery_date : null,
            priority_level: formData.priority_level,
            price_list_id: formData.price_list_id && formData.price_list_id.trim() !== '' ? formData.price_list_id : null,
            customer_notes: formData.customer_notes || null
          })
          .select()
          .single();

        if (poError) throw poError;
        poId = newPO.id;
      } else {
        // Update existing PI
        const { error: poUpdateError } = await supabase
          .from('customer_purchase_orders')
          .update({
            po_number: formData.customer_po_number || purchaseOrder.po_number,
            status: formData.status || purchaseOrder.status || 'draft',
            customer_id: formData.customer_id,
            customer_name: customer?.name,
            customer_company: customer?.company,
            customer_po_number: formData.customer_po_number || null,
            order_date: formData.order_date && formData.order_date.trim() !== '' ? formData.order_date : null,
            requested_delivery_date: formData.requested_delivery_date && formData.requested_delivery_date.trim() !== '' ? formData.requested_delivery_date : null,
            priority_level: formData.priority_level,
            price_list_id: formData.price_list_id && formData.price_list_id.trim() !== '' ? formData.price_list_id : null,
            customer_notes: formData.customer_notes || null,
            current_stage: 'po_creation',
            stage_completed: purchaseOrder.stage_completed ? 
              [...purchaseOrder.stage_completed, 'order_creation'].filter((v, i, a) => a.indexOf(v) === i) : 
              ['order_creation']
          })
          .eq('id', poId);

        if (poUpdateError) throw poUpdateError;

        // Update all po_creation records to reflect the new PI number
        // This ensures BOL, invoices, and other components see the updated number
        if (formData.customer_po_number && formData.customer_po_number !== purchaseOrder.po_number) {
          const { error: poCreationUpdateError } = await supabase
            .from('po_creation')
            .update({ po_number: formData.customer_po_number })
            .eq('purchase_order_id', poId);

          if (poCreationUpdateError) {
            console.error('Error updating po_creation records:', poCreationUpdateError);
            // Don't throw - this is not critical, just log it
          }

          // Update BOL number to match the new PI number
          // Extract number from PI (e.g., PI-2025-0002 -> BOL-2025-0002)
          const piMatch = formData.customer_po_number.match(/PI-(\d{4})-(\d+)/);
          if (piMatch) {
            const year = piMatch[1];
            const number = piMatch[2];
            const newBolNumber = `BOL-${year}-${number}`;
            
            const { error: bolUpdateError } = await supabase
              .from('po_delivery_information')
              .update({ bol_number: newBolNumber })
              .eq('purchase_order_id', poId);

            if (bolUpdateError) {
              console.error('Error updating BOL number:', bolUpdateError);
              // Don't throw - this is not critical, just log it
            }
          }
        }
      }

      // Now insert/update order creation record
      const { data: existing } = await supabase
        .from('po_order_creation')
        .select('id')
        .eq('purchase_order_id', poId)
        .maybeSingle();

      const orderCreationData = {
        company_id: companyId,
        purchase_order_id: poId,
        customer_id: formData.customer_id,
        customer_po_number: formData.customer_po_number || null,
        order_date: formData.order_date && formData.order_date.trim() !== '' ? formData.order_date : null,
        requested_delivery_date: formData.requested_delivery_date && formData.requested_delivery_date.trim() !== '' ? formData.requested_delivery_date : null,
        priority_level: formData.priority_level,
        customer_notes: formData.customer_notes || null,
        price_list_id: formData.price_list_id && formData.price_list_id.trim() !== '' ? formData.price_list_id : null,
        payment_terms: formData.payment_terms,
        payment_method: formData.payment_method || null,
        stage_completed: true
      };

      if (existing) {
        const { error: updateError } = await supabase
          .from('po_order_creation')
          .update(orderCreationData)
          .eq('id', existing.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('po_order_creation')
          .insert(orderCreationData);

        if (insertError) throw insertError;
      }

      // Reload the selected customer to show all details
      if (formData.customer_id && customers.length > 0) {
        const customer = customers.find(c => c.id === formData.customer_id);
        if (customer) {
          setSelectedCustomer(customer);
        }
      }

      // Notify parent - pass the PI ID!
      if (onUpdate) {
        onUpdate(poId);
      }
      
      setEditDialogOpen(false);

    } catch (err) {
      console.error('Error saving order creation:', err);
      setError('Failed to save order information: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id,
      // Auto-populate payment terms from customer if available
      payment_terms: customer.payment_terms || prev.payment_terms
    }));
    setCustomerDialogOpen(false);
  };

  const handlePriceListSelect = (priceListId) => {
    setFormData(prev => ({
      ...prev,
      price_list_id: priceListId
    }));
    setPriceListDialogOpen(false);
  };

  const getFilteredCustomers = () => {
    if (!customerSearchTerm.trim()) return customers;
    
    const term = customerSearchTerm.toLowerCase();
    return customers.filter(customer => 
      customer.name?.toLowerCase().includes(term) ||
      customer.email?.toLowerCase().includes(term) ||
      customer.phone?.includes(term) ||
      customer.company?.toLowerCase().includes(term)
    );
  };

  const getFilteredPriceLists = () => {
    if (!priceListSearchTerm.trim()) return priceLists;
    
    const term = priceListSearchTerm.toLowerCase();
    return priceLists.filter(pl => 
      pl.name?.toLowerCase().includes(term) ||
      pl.type?.toLowerCase().includes(term)
    );
  };

  const getSelectedPriceList = () => {
    return priceLists.find(pl => pl.id === formData.price_list_id);
  };

  // Access Denied if no view permission
  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '50vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Order Creation Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to view Order Creation. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  // Show simple placeholder if not in dialog
  if (!editDialogOpen && !purchaseOrder?.id) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Loading Order Form...
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {/* Display View - Show order info ALWAYS (dialog renders on top) */}
        {purchaseOrder?.id && (
          <Paper sx={{ p: 4, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
                Order Information
              </Typography>
              {canEdit && (
                <Button 
                  variant="outlined" 
                  onClick={() => {
                    // Reload data before opening dialog
                    loadOrderCreationData();
                    setEditDialogOpen(true);
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  Edit Details
                </Button>
              )}
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Customer Information
                </Typography>
                <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
                        {selectedCustomer?.name || purchaseOrder.customer_name || '—'}
                      </Typography>
                      {(selectedCustomer?.company || purchaseOrder.customer_company) && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {selectedCustomer?.company || purchaseOrder.customer_company}
                        </Typography>
                      )}
                    </Grid>
                    {selectedCustomer?.email && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Email</Typography>
                        <Typography variant="body2">{selectedCustomer.email}</Typography>
                      </Grid>
                    )}
                    {selectedCustomer?.phone && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Phone</Typography>
                        <Typography variant="body2">{selectedCustomer.phone}</Typography>
                      </Grid>
                    )}
                    {(selectedCustomer?.street || selectedCustomer?.city || selectedCustomer?.state) && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Address</Typography>
                        <Typography variant="body2">
                          {selectedCustomer.street && `${selectedCustomer.street}, `}
                          {selectedCustomer.city && `${selectedCustomer.city}, `}
                          {selectedCustomer.state && `${selectedCustomer.state} `}
                          {selectedCustomer.zip_code}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Order Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formatDate(formData.order_date)}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Requested Delivery
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formatDate(formData.requested_delivery_date)}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Priority Level
                </Typography>
                <Chip 
                  label={formData.priority_level?.toUpperCase() || 'MEDIUM'} 
                  color={formData.priority_level === 'urgent' ? 'error' : formData.priority_level === 'high' ? 'warning' : 'default'}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Status
                </Typography>
                {getStatusChip(formData.status || purchaseOrder?.status || 'draft')}
              </Grid>

              {formData.customer_po_number && (
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    PI #
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formData.customer_po_number}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Payment Terms
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formData.payment_terms || '—'}
                </Typography>
              </Grid>

              {formData.payment_method && (
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Payment Method
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formData.payment_method}
                  </Typography>
                </Grid>
              )}

              {formData.customer_notes && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Customer Notes
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2">
                      {formData.customer_notes}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {/* Main Edit Dialog - JUST LIKE FORMULATIONS! */}
        <Dialog
          open={editDialogOpen}
          onClose={isCreatingNew ? undefined : () => setEditDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: 'none',
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
              backdropFilter: 'blur(20px)',
              boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{ 
            p: 4, 
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
            color: theme.palette.text.primary,
            fontWeight: 500
          }}>
            {isCreatingNew ? 'Create New Customer Order' : 'Edit Order Details'}
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            {!canEdit && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                You don't have permission to edit order creation. All fields are read-only.
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Grid container spacing={3} sx={{ mt: 0.5 }}>
              {/* Customer Selection */}
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setCustomerDialogOpen(true)}
                  sx={{
                    height: 80,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    justifyContent: 'flex-start',
                    px: 3,
                    py: 2,
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <PersonIcon sx={{ mr: 2, color: 'text.secondary', fontSize: 32 }} />
                    <Box sx={{ textAlign: 'left', flex: 1 }}>
                      {selectedCustomer ? (
                        <>
                          <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1rem', mb: 0.5 }}>
                            {selectedCustomer.name}
                          </Typography>
                          {selectedCustomer.company && (
                            <Typography variant="body2" color="text.secondary">
                              {selectedCustomer.company}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'error.main' }}>
                          Click to Select Customer *
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Button>
              </Grid>

              {/* Order Details */}
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Order Date *"
                  value={formData.order_date ? dayjs(formData.order_date) : null}
                  onChange={(newValue) => handleInputChange('order_date', newValue ? newValue.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !formData.order_date,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          '& fieldset': { border: 'none' }
                        }
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Requested Delivery Date *"
                  value={formData.requested_delivery_date ? dayjs(formData.requested_delivery_date) : null}
                  onChange={(newValue) => handleInputChange('requested_delivery_date', newValue ? newValue.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !formData.requested_delivery_date,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          '& fieldset': { border: 'none' }
                        }
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Proforma Invoice Number *"
                  fullWidth
                  required
                  error={!formData.customer_po_number}
                  value={formData.customer_po_number}
                  onChange={(e) => handleInputChange('customer_po_number', e.target.value)}
                  placeholder="PI-2025-0001"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& fieldset': { border: 'none' }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!formData.priority_level}>
                  <InputLabel>Priority Level *</InputLabel>
                  <Select
                    value={formData.priority_level}
                    label="Priority Level *"
                    onChange={(e) => handleInputChange('priority_level', e.target.value)}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                    }}
                  >
                    <MenuItem value="low">Low Priority</MenuItem>
                    <MenuItem value="medium">Medium Priority</MenuItem>
                    <MenuItem value="high">High Priority</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status || 'draft'}
                    label="Status"
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    disabled={(formData.status || purchaseOrder?.status || 'draft') === 'approved' || 
                               (formData.status || purchaseOrder?.status || 'draft') === 'shipped' ||
                               (formData.status || purchaseOrder?.status || 'draft') === 'delivered'}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: (formData.status || purchaseOrder?.status || 'draft') === 'approved' ||
                                  (formData.status || purchaseOrder?.status || 'draft') === 'shipped' ||
                                  (formData.status || purchaseOrder?.status || 'draft') === 'delivered'
                        ? alpha(theme.palette.grey[200], 0.5) 
                        : 'white',
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&.Mui-disabled': {
                        bgcolor: alpha(theme.palette.grey[200], 0.5),
                        color: theme.palette.text.secondary
                      }
                    }}
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="pending_approval">Pending Approval</MenuItem>
                    <MenuItem value="in_production">In Production</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
                {((formData.status || purchaseOrder?.status || 'draft') === 'approved' ||
                  (formData.status || purchaseOrder?.status || 'draft') === 'shipped' ||
                  (formData.status || purchaseOrder?.status || 'draft') === 'delivered') && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Status cannot be changed once approved, shipped, or delivered
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Customer Notes"
                  multiline
                  rows={3}
                  fullWidth
                  value={formData.customer_notes}
                  onChange={(e) => handleInputChange('customer_notes', e.target.value)}
                  placeholder="Any special instructions or notes from the customer..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& fieldset': { border: 'none' }
                    }
                  }}
                />
              </Grid>

              {/* Payment Terms */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo
                  value={formData.payment_terms}
                  onChange={(event, newValue) => {
                    handleInputChange('payment_terms', newValue || '');
                  }}
                  onInputChange={(event, newInputValue) => {
                    handleInputChange('payment_terms', newInputValue);
                  }}
                  options={['Net 30', 'Net 60', 'COD', 'Prepaid']}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Payment Terms *"
                      required
                      error={!formData.payment_terms}
                      placeholder="Select or type custom terms"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          '& fieldset': { border: 'none' }
                        }
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo
                  value={formData.payment_method}
                  onChange={(event, newValue) => {
                    handleInputChange('payment_method', newValue || '');
                  }}
                  onInputChange={(event, newInputValue) => {
                    handleInputChange('payment_method', newInputValue);
                  }}
                  options={['Check', 'Credit Card', 'Bank Transfer', 'Cash', 'Wire Transfer']}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Payment Method *"
                      required
                      error={!formData.payment_method}
                      placeholder="Select or type custom method"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          '& fieldset': { border: 'none' }
                        }
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ 
            p: 4, 
            gap: 2, 
            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
            justifyContent: 'space-between'
          }}>
            <Button 
              onClick={() => navigate(`/company/${companyId}/proforma-invoices`)}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                color: theme.palette.text.secondary,
                fontWeight: 500
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!canEdit || loading || !formData.customer_id || !formData.order_date || !formData.requested_delivery_date || !formData.customer_po_number || !formData.priority_level || !formData.payment_terms || !formData.payment_method}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                color: 'white',
                fontWeight: 500,
                '&:disabled': {
                  background: alpha(theme.palette.grey[400], 0.5)
                }
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : isCreatingNew ? 'Create Customer Order' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Customer Selection Dialog */}
        <Dialog
          open={customerDialogOpen}
          onClose={() => setCustomerDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: theme.palette.grey[100],
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              height: '70vh',
              maxHeight: 700
            }
          }}
        >
          {/* Header */}
          <Box sx={{
            px: 3, py: 2.5,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.primary' }}>
                Select Customer
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
                {getFilteredCustomers().length} customer{getFilteredCustomers().length !== 1 ? 's' : ''} available
              </Typography>
            </Box>
            <IconButton onClick={() => setCustomerDialogOpen(false)} sx={{ color: 'text.secondary' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search */}
          <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`, background: 'white' }}>
            <TextField
              fullWidth
              placeholder="Search by name, company, or email..."
              value={customerSearchTerm}
              onChange={(e) => setCustomerSearchTerm(e.target.value)}
              autoFocus
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: { fontSize: '0.9rem', fontWeight: 500 }
              }}
              sx={{
                minWidth: 280,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '& fieldset': { border: 'none' },
                  '&:hover': { boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}` },
                  '&.Mui-focused': { boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}` }
                }
              }}
            />
          </Box>

          {/* Table */}
          <DialogContent sx={{ p: 0, overflowY: 'auto', overflowX: 'hidden' }}>
            <TableContainer sx={{ overflowX: 'hidden' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Customer', 'Company', 'Email', 'Phone'].map((col) => (
                      <TableCell key={col} sx={{
                        fontWeight: 500, fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 1.5, px: 2.5
                      }}>
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredCustomers().length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          No customers found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    getFilteredCustomers().map((customer) => (
                      <TableRow
                        key={customer.id}
                        hover
                        onClick={() => handleCustomerSelect(customer)}
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          bgcolor: selectedCustomer?.id === customer.id ? alpha(theme.palette.primary.main, 0.04) : 'white',
                          '&:hover': {
                            bgcolor: 'grey.50',
                            transform: 'translateX(4px)',
                            boxShadow: 'inset 4px 0 0 black'
                          },
                          '& .MuiTableCell-root': {
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                            py: 2, px: 2.5
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem', color: 'text.primary' }}>
                              {customer.name}
                            </Typography>
                            {selectedCustomer?.id === customer.id && (
                              <Chip label="Selected" size="small" color="primary" sx={{ fontSize: '0.7rem', height: 20 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                            {customer.company || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                            {customer.email || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                            {customer.phone || '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
});

export default OrderCreation;